import type { ReportQuery, ReportRecordsQuery } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { ApiError } from '../../../shared/shared.api';
import { exportReport } from '../reports.api';
import type { ReportTab } from '../reports.paths';

function messageOf(error: unknown): string {
  const body = error instanceof ApiError ? (error.body as { message?: unknown } | undefined) : undefined;
  return typeof body?.message === 'string' ? body.message : 'The export did not work. Try again.';
}

function save(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Downloads the tab's rows under its filters, search and sort. A refusal (over the row limit) stays inline, beside the button; success is a toast. */
export function useReportExport(report: { key: string; title: string }, tab: ReportTab) {
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(query: ReportQuery | ReportRecordsQuery): Promise<void> {
    setIsExporting(true);
    setError(null);
    try {
      const file = await exportReport(report.key, tab, query);
      save(file.blob, file.filename);
      toast.success(`Exported ${report.title} ${tab} (${file.rowCount.toLocaleString('en-US')} rows)`);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setIsExporting(false);
    }
  }

  return { isExporting, error, start };
}
